import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "../contexts/AuthContext";
import axios from "axios";
import { Link } from "react-router-dom";
import { 
  Users, Trophy, Zap, TrendingUp, Calendar, ChevronLeft, 
  Loader2, Crown, Target, Award, Eye, ChevronDown, ChevronUp, Star,
  ClipboardCopy, AlertTriangle, Search, RefreshCw
} from "lucide-react";

export default function AdminAnalyticsPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [error, setError] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDailyHistory, setUserDailyHistory] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [expandedLeagues, setExpandedLeagues] = useState(false);

  useEffect(() => {
    const fetchAnalytics = async () => {
      if (!user) return;
      try {
        const token = await user.getIdToken();
        const res = await axios.get(`${API}/admin/analytics`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAnalytics(res.data);
      } catch (e) {
        setError(e.response?.data?.detail || "Failed to load analytics");
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, [user]);

  const fetchUserDailyHistory = async (userId, userName) => {
    setLoadingHistory(true);
    setSelectedUser({ id: userId, name: userName });
    try {
      const token = await user.getIdToken();
      const res = await axios.get(`${API}/admin/users/${userId}/daily-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserDailyHistory(res.data);
    } catch (e) {
      console.error("Failed to fetch user history:", e);
    } finally {
      setLoadingHistory(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="animate-spin text-emerald-500" size={48} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 text-center">
          <p className="text-red-400">{error}</p>
          <Link to="/admin" className="text-emerald-400 hover:underline mt-2 inline-block">
            Back to Admin
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-6" data-testid="admin-analytics-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Link to="/admin" className="p-2 hover:bg-slate-800 rounded-lg transition-colors">
          <ChevronLeft className="text-slate-400" size={20} />
        </Link>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
            <TrendingUp className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">Analytics Dashboard</h1>
            <p className="text-sm text-slate-400">Platform statistics & insights</p>
          </div>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard 
          icon={<Users size={18} />} 
          label="Total Users" 
          value={analytics.overview.total_users} 
          color="emerald" 
        />
        <StatCard 
          icon={<Users size={18} />} 
          label="Users Today" 
          value={analytics.overview.users_today} 
          color="cyan" 
        />
        <StatCard 
          icon={<Trophy size={18} />} 
          label="Weekly Teams" 
          value={analytics.overview.weekly_participants} 
          color="amber" 
        />
        <StatCard 
          icon={<Zap size={18} />} 
          label="Daily Players" 
          value={analytics.overview.daily_participants} 
          color="orange" 
        />
        <StatCard 
          icon={<Target size={18} />} 
          label="Leagues" 
          value={analytics.overview.total_leagues} 
          color="purple" 
        />
        <StatCard 
          icon={<Users size={18} />} 
          label="In Leagues" 
          value={analytics.overview.total_league_members} 
          color="blue" 
        />
      </div>

      {/* Today's Activity */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
        <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
          <Calendar size={18} className="text-cyan-400" />
          Today's Activity
        </h2>
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center">
            <p className="text-2xl font-bold text-cyan-400">{analytics.today.new_users}</p>
            <p className="text-xs text-slate-400">New Users</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-amber-400">{analytics.today.daily_teams_created}</p>
            <p className="text-xs text-slate-400">Daily Teams</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-emerald-400">{analytics.today.weekly_teams_activity}</p>
            <p className="text-xs text-slate-400">Weekly Activity</p>
          </div>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Top Weekly Performers */}
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
          <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
            <Trophy size={18} className="text-amber-400" />
            Top Season Performers
          </h2>
          <div className="space-y-2">
            {analytics.top_weekly_users.map((u, idx) => (
              <div key={u.id} className="flex items-center justify-between p-2 rounded-lg bg-slate-700/30 hover:bg-slate-700/50 transition-colors">
                <div className="flex items-center gap-2">
                  <span className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold ${
                    idx === 0 ? 'bg-yellow-500 text-black' :
                    idx === 1 ? 'bg-slate-400 text-black' :
                    idx === 2 ? 'bg-amber-700 text-white' :
                    'bg-slate-600 text-white'
                  }`}>{idx + 1}</span>
                  <div>
                    <p className="text-white text-sm font-medium">{u.team_name || u.display_name}</p>
                  </div>
                </div>
                <span className="text-amber-400 font-mono font-bold">{u.total_points}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Top Daily Performers */}
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
          <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
            <Zap size={18} className="text-orange-400" />
            Top Daily Performers
          </h2>
          <div className="space-y-2">
            {analytics.top_daily_users.length === 0 ? (
              <p className="text-slate-500 text-sm text-center py-4">No daily participants yet</p>
            ) : (
              analytics.top_daily_users.map((u, idx) => (
                <div 
                  key={u.id} 
                  className="flex items-center justify-between p-2 rounded-lg bg-slate-700/30 hover:bg-slate-700/50 transition-colors cursor-pointer"
                  onClick={() => fetchUserDailyHistory(u.id, u.team_name || u.display_name)}
                >
                  <div className="flex items-center gap-2">
                    <span className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold ${
                      idx === 0 ? 'bg-orange-500 text-black' :
                      idx === 1 ? 'bg-slate-400 text-black' :
                      idx === 2 ? 'bg-amber-700 text-white' :
                      'bg-slate-600 text-white'
                    }`}>{idx + 1}</span>
                    <div>
                      <p className="text-white text-sm font-medium">{u.team_name || u.display_name}</p>
                      <p className="text-xs text-slate-500">{u.daily_matches_played} matches</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-orange-400 font-mono font-bold">{u.daily_total_points}</span>
                    <Eye size={14} className="text-slate-500" />
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* This Week's Top Performers */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
        <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
          <Star size={18} className="text-cyan-400" />
          Week {analytics.current_week_number} Top Performers
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-2">
          {(!analytics.top_current_week || analytics.top_current_week.length === 0) ? (
            <p className="text-slate-500 text-sm col-span-full text-center py-4">No data for this week yet</p>
          ) : (
            analytics.top_current_week.map((u, idx) => (
              <div key={u.id} className="flex items-center justify-between p-2 rounded-lg bg-slate-700/30">
                <div className="flex items-center gap-2">
                  <span className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold ${
                    idx === 0 ? 'bg-cyan-500 text-black' :
                    idx === 1 ? 'bg-slate-400 text-black' :
                    idx === 2 ? 'bg-amber-700 text-white' :
                    'bg-slate-600 text-white'
                  }`}>{idx + 1}</span>
                  <p className="text-white text-sm font-medium truncate max-w-[100px]">{u.team_name || u.display_name}</p>
                </div>
                <span className="text-cyan-400 font-mono font-bold text-sm">{u.week_points}</span>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Daily Match Participation */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
        <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
          <Target size={18} className="text-cyan-400" />
          Daily Match Participation
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {analytics.daily_match_participation.length === 0 ? (
            <p className="text-slate-500 text-sm col-span-full text-center py-4">No daily matches played yet</p>
          ) : (
            analytics.daily_match_participation.map((m) => (
              <div key={m.match_id} className="bg-slate-700/30 rounded-lg p-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-white font-medium text-sm">{m.team1} vs {m.team2}</span>
                  <span className="text-cyan-400 font-bold">{m.teams}</span>
                </div>
                <p className="text-xs text-slate-500">{m.date}</p>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Leagues */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
        <button 
          onClick={() => setExpandedLeagues(!expandedLeagues)}
          className="w-full flex items-center justify-between"
        >
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Award size={18} className="text-purple-400" />
            Leagues ({analytics.leagues.length})
          </h2>
          {expandedLeagues ? <ChevronUp className="text-slate-400" /> : <ChevronDown className="text-slate-400" />}
        </button>
        
        {expandedLeagues && (
          <div className="mt-4 space-y-2">
            {analytics.leagues.map((league) => (
              <div key={league.id} className="flex items-center justify-between p-2 rounded-lg bg-slate-700/30">
                <span className="text-white text-sm">{league.name}</span>
                <span className="text-purple-400 font-mono">{league.member_count} members</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* User Daily History Modal */}
      {selectedUser && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b border-slate-700 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-white">{selectedUser.name}'s Daily History</h3>
                {userDailyHistory && (
                  <p className="text-sm text-slate-400">
                    {userDailyHistory.user.daily_matches_played} matches • {userDailyHistory.user.daily_total_points} pts
                  </p>
                )}
              </div>
              <button 
                onClick={() => { setSelectedUser(null); setUserDailyHistory(null); }}
                className="p-2 hover:bg-slate-700 rounded-lg"
              >
                <ChevronLeft size={20} className="text-slate-400" />
              </button>
            </div>
            
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              {loadingHistory ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="animate-spin text-emerald-500" size={32} />
                </div>
              ) : userDailyHistory ? (
                <div className="space-y-3">
                  {userDailyHistory.daily_teams.map((team) => (
                    <div key={team.id || team.match_id} className="bg-slate-700/30 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <p className="text-white font-medium">
                            {team.team1_short || team.team1} vs {team.team2_short || team.team2}
                          </p>
                          <p className="text-xs text-slate-500">{team.match_date}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-amber-400">{team.points ?? '-'}</p>
                          <p className="text-xs text-slate-500">points</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-1 text-xs">
                        <div className="text-slate-400">
                          <span className="text-yellow-400">C:</span> {team.captain_name}
                        </div>
                        <div className="text-slate-400">
                          <span className="text-cyan-400">VC:</span> {team.vice_captain_name}
                        </div>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1">
                        {team.player_names?.map((name, idx) => (
                          <span key={idx} className="text-xs bg-slate-600/50 px-1.5 py-0.5 rounded text-slate-300">
                            {name}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-400 text-center py-8">No history found</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Points Audit Table */}
      <PointsAuditTable user={user} />
    </div>
  );
}

function PointsAuditTable({ user }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [matchId, setMatchId] = useState("");
  const [week, setWeek] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [copied, setCopied] = useState(false);
  const [captainChangeLogs, setCaptainChangeLogs] = useState([]);
  const [loadingCaptainLogs, setLoadingCaptainLogs] = useState(false);
  const [showCaptainLogs, setShowCaptainLogs] = useState(false);
  const [captainLogWeekFilter, setCaptainLogWeekFilter] = useState("");
  const [fixingHistory, setFixingHistory] = useState(false);
  const [fixResult, setFixResult] = useState(null);

  const fetchAudit = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const token = await user.getIdToken();
      const params = new URLSearchParams();
      if (matchId) params.set("match_id", matchId);
      if (week) params.set("week", week);
      const res = await axios.get(`${API}/admin/points-audit?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (e) {
      console.error("Audit fetch failed:", e);
    } finally {
      setLoading(false);
    }
  }, [user, matchId, week]);

  useEffect(() => { fetchAudit(); }, [fetchAudit]);

  const fetchCaptainChangeLogs = async (weekFilter) => {
    setLoadingCaptainLogs(true);
    try {
      const token = await user.getIdToken();
      const params = new URLSearchParams();
      const wf = weekFilter !== undefined ? weekFilter : captainLogWeekFilter;
      if (wf) params.set("week", wf);
      const res = await axios.get(`${API}/admin/captain-change-log?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCaptainChangeLogs(res.data.logs || []);
    } catch (e) {
      console.error("Failed to fetch captain change logs:", e);
    } finally {
      setLoadingCaptainLogs(false);
    }
  };

  const [editingWeekLogId, setEditingWeekLogId] = useState(null);
  const [editingWeekValue, setEditingWeekValue] = useState("");

  const handleSaveWeek = async (logId) => {
    const week = parseInt(editingWeekValue);
    if (!week || week < 1) return;
    try {
      const token = await user.getIdToken();
      await axios.patch(`${API}/admin/captain-change-log/${logId}/week`, { week }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCaptainChangeLogs(prev => prev.map(l => l.id === logId ? { ...l, week } : l));
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to update week");
    }
    setEditingWeekLogId(null);
  };

  const handleFixCaptainHistory = async () => {
    if (!window.confirm("This will recalculate points for all users who changed captain/VC using their correct per-week captain. Continue?")) return;
    setFixingHistory(true);
    setFixResult(null);
    try {
      const token = await user.getIdToken();
      const res = await axios.post(`${API}/admin/fix-captain-history`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFixResult(res.data);
      fetchAudit(); // Refresh points audit
    } catch (e) {
      setFixResult({ error: e.response?.data?.detail || "Fix failed" });
    } finally {
      setFixingHistory(false);
    }
  };

  const [recalculating, setRecalculating] = useState(false);
  const [recalcResult, setRecalcResult] = useState(null);

  const handleForceRecalcSnapshots = async () => {
    if (!window.confirm("This will recalculate ALL purchase snapshots for every user in the active tournament. Continue?")) return;
    setRecalculating(true);
    setRecalcResult(null);
    try {
      const token = await user.getIdToken();
      const res = await axios.post(`${API}/admin/force-recalculate-snapshots`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecalcResult(res.data);
    } catch (e) {
      setRecalcResult({ error: e.response?.data?.detail || "Recalculation failed" });
    } finally {
      setRecalculating(false);
    }
  };

  const filteredRows = (data?.rows || []).filter(r => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (r.display_name || "").toLowerCase().includes(s) || 
           (r.team_name || "").toLowerCase().includes(s) ||
           (r.user_id || "").toLowerCase().includes(s);
  });

  const corruptedCount = (data?.rows || []).filter(r => r.difference !== 0).length;

  const copyCSV = () => {
    const headers = [
      "user_id", "display_name", "team_name", "players", "captain", "vice_captain",
      matchId ? `match_pts_${matchId}` : null,
      matchId ? "player_points" : null,
      week ? `week_pts_${week}` : null,
      "team_total_points", "sum_match_points", "user_total_points",
      "referral_points", "predictor_points", "adjustment_points", "difference"
    ].filter(Boolean);
    
    const csvRows = filteredRows.map(r => [
      r.user_id, `"${r.display_name}"`, `"${r.team_name}"`,
      `"${r.players}"`, `"${r.captain}"`, `"${r.vice_captain}"`,
      matchId ? (r.match_points ?? 0) : null,
      matchId ? `"${r.player_points || ''}"` : null,
      week ? (r.week_points ?? 0) : null,
      r.team_total_points, r.sum_match_points, r.user_total_points,
      r.referral_points, r.predictor_points, r.adjustment_points, r.difference
    ].filter(v => v !== null).join(","));
    
    const csv = [headers.join(","), ...csvRows].join("\n");
    // Fallback: use textarea + execCommand for environments where Clipboard API is blocked
    try {
      const textarea = document.createElement("textarea");
      textarea.value = csv;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.error("Copy failed:", e);
    }
  };

  return (
    <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4" data-testid="points-audit-table">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="text-lg font-semibold text-white flex items-center gap-2">
          <AlertTriangle size={18} className="text-red-400" />
          Points Audit Table
          {corruptedCount > 0 && (
            <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded-full">
              {corruptedCount} mismatched
            </span>
          )}
        </h2>
        <button
          onClick={copyCSV}
          disabled={!data?.rows?.length}
          className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg text-xs flex items-center gap-1.5 transition-colors disabled:opacity-50"
          data-testid="copy-csv-btn"
        >
          <ClipboardCopy size={14} />
          {copied ? "Copied!" : "Copy as CSV"}
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        <select
          value={matchId}
          onChange={e => setMatchId(e.target.value)}
          className="bg-slate-700 border border-slate-600 text-white text-sm rounded-lg px-3 py-1.5 focus:outline-none focus:border-emerald-500"
          data-testid="audit-match-filter"
        >
          <option value="">All Matches</option>
          {(data?.matches || []).map(m => (
            <option key={m.match_id} value={m.match_id}>
              #{m.match_number} {m.team1_short || m.team1} vs {m.team2_short || m.team2}
            </option>
          ))}
        </select>

        <select
          value={week}
          onChange={e => setWeek(e.target.value)}
          className="bg-slate-700 border border-slate-600 text-white text-sm rounded-lg px-3 py-1.5 focus:outline-none focus:border-emerald-500"
          data-testid="audit-week-filter"
        >
          <option value="">All Weeks</option>
          {(data?.weeks || []).map(w => (
            <option key={w} value={w}>
              Week {w.replace("week_", "")}
            </option>
          ))}
        </select>

        <div className="relative flex-1 min-w-[180px]">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            type="text"
            placeholder="Search user or team..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full bg-slate-700 border border-slate-600 text-white text-sm rounded-lg pl-8 pr-3 py-1.5 focus:outline-none focus:border-emerald-500"
            data-testid="audit-search"
          />
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="animate-spin text-emerald-500" size={32} />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-slate-600">
                <th className="text-left text-slate-400 font-medium py-2 px-2 whitespace-nowrap">User</th>
                <th className="text-left text-slate-400 font-medium py-2 px-2 whitespace-nowrap">Team</th>
                <th className="text-left text-slate-400 font-medium py-2 px-2 whitespace-nowrap">Players</th>
                <th className="text-left text-slate-400 font-medium py-2 px-2 whitespace-nowrap">C</th>
                <th className="text-left text-slate-400 font-medium py-2 px-2 whitespace-nowrap">VC</th>
                {matchId && <th className="text-right text-slate-400 font-medium py-2 px-2 whitespace-nowrap">Match Pts</th>}
                {matchId && <th className="text-left text-slate-400 font-medium py-2 px-2 whitespace-nowrap">Player Points</th>}
                {week && <th className="text-right text-slate-400 font-medium py-2 px-2 whitespace-nowrap">Week Pts</th>}
                <th className="text-right text-slate-400 font-medium py-2 px-2 whitespace-nowrap">Team Total</th>
                <th className="text-right text-slate-400 font-medium py-2 px-2 whitespace-nowrap">Sum Match</th>
                <th className="text-right text-slate-400 font-medium py-2 px-2 whitespace-nowrap">User Total</th>
                <th className="text-right text-slate-400 font-medium py-2 px-2 whitespace-nowrap">Referral</th>
                <th className="text-right text-slate-400 font-medium py-2 px-2 whitespace-nowrap">Predictor</th>
                <th className="text-right text-slate-400 font-medium py-2 px-2 whitespace-nowrap">Adjust</th>
                <th className="text-right text-slate-400 font-medium py-2 px-2 whitespace-nowrap">Diff</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.length === 0 ? (
                <tr><td colSpan={15} className="text-center py-8 text-slate-500">No data</td></tr>
              ) : (
                filteredRows.map(r => (
                  <tr key={r.user_id} className={`border-b border-slate-700/50 ${r.difference !== 0 ? 'bg-red-500/5' : 'hover:bg-slate-700/30'}`}>
                    <td className="py-2 px-2">
                      <p className="text-white font-medium truncate max-w-[120px]">{r.display_name || r.user_id.slice(0, 8)}</p>
                    </td>
                    <td className="py-2 px-2 text-slate-300 truncate max-w-[100px]">{r.team_name}</td>
                    <td className="py-2 px-2 text-slate-400 text-[10px] max-w-[200px]">
                      <span className="line-clamp-2">{r.players}</span>
                    </td>
                    <td className="py-2 px-2 text-yellow-400 text-[10px] whitespace-nowrap">{r.captain}</td>
                    <td className="py-2 px-2 text-cyan-400 text-[10px] whitespace-nowrap">{r.vice_captain}</td>
                    {matchId && <td className="py-2 px-2 text-right text-slate-300 font-mono">{r.match_points ?? 0}</td>}
                    {matchId && (
                      <td className="py-2 px-2 text-[10px] text-slate-400 max-w-[250px]">
                        <span className="line-clamp-2">{r.player_points || '-'}</span>
                      </td>
                    )}
                    {week && <td className="py-2 px-2 text-right text-slate-300 font-mono">{r.week_points ?? 0}</td>}
                    <td className="py-2 px-2 text-right text-amber-400 font-mono font-medium">{r.team_total_points}</td>
                    <td className="py-2 px-2 text-right text-slate-300 font-mono">{r.sum_match_points}</td>
                    <td className="py-2 px-2 text-right text-cyan-400 font-mono font-medium">{r.user_total_points}</td>
                    <td className="py-2 px-2 text-right text-slate-400 font-mono">{r.referral_points}</td>
                    <td className="py-2 px-2 text-right text-slate-400 font-mono">{r.predictor_points}</td>
                    <td className="py-2 px-2 text-right text-slate-400 font-mono">{r.adjustment_points}</td>
                    <td className={`py-2 px-2 text-right font-mono font-bold ${r.difference !== 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                      {r.difference}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <p className="text-xs text-slate-500 mt-2">{filteredRows.length} users shown</p>
        </div>
      )}

      {/* Captain/VC Change Audit Log */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-white font-semibold flex items-center gap-2">
            <Crown size={16} className="text-amber-400" />
            Captain / Vice Captain Changes
          </h2>
          <button
            onClick={() => { setShowCaptainLogs(!showCaptainLogs); if (!showCaptainLogs && captainChangeLogs.length === 0) fetchCaptainChangeLogs(); }}
            className="flex items-center gap-1 text-xs text-emerald-400 hover:text-emerald-300"
            data-testid="toggle-captain-logs"
          >
            {showCaptainLogs ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            {showCaptainLogs ? "Hide" : "Show"}
          </button>
        </div>

        {showCaptainLogs && (
          <div>
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <select
                  value={captainLogWeekFilter}
                  onChange={(e) => { setCaptainLogWeekFilter(e.target.value); fetchCaptainChangeLogs(e.target.value); }}
                  className="px-2 py-1 bg-slate-900 border border-slate-600 rounded text-xs text-white"
                  data-testid="captain-log-week-filter"
                >
                  <option value="">All Weeks</option>
                  {[1,2,3,4,5,6,7,8].map(w => (
                    <option key={w} value={w}>Week {w}</option>
                  ))}
                </select>
                <button
                  onClick={() => fetchCaptainChangeLogs()}
                  className="flex items-center gap-1 text-xs text-slate-400 hover:text-white"
                  data-testid="refresh-captain-logs"
                >
                  <RefreshCw size={12} className={loadingCaptainLogs ? "animate-spin" : ""} />
                  Refresh
                </button>
              </div>
              <button
                disabled={true}
                className="flex items-center gap-1 px-3 py-1.5 bg-slate-700/50 text-slate-500 rounded-lg text-xs font-medium cursor-not-allowed line-through"
                data-testid="fix-captain-history-btn"
                title="Disabled — this endpoint corrupts historical points"
              >
                <AlertTriangle size={12} />
                Fix Captain History (Disabled)
              </button>
              <button
                onClick={handleForceRecalcSnapshots}
                disabled={recalculating}
                className="flex items-center gap-1 px-3 py-1.5 bg-amber-500/20 text-amber-400 rounded-lg text-xs font-medium hover:bg-amber-500/30 disabled:opacity-50"
                data-testid="force-recalc-snapshots-btn"
              >
                {recalculating ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                Recalculate All Budgets
              </button>
            </div>

            {fixResult && (
              <div className={`mb-3 p-3 rounded-lg text-xs ${fixResult.error ? 'bg-red-500/10 border border-red-500/30 text-red-400' : 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-400'}`}>
                {fixResult.error ? (
                  <p>{fixResult.error}</p>
                ) : (
                  <div>
                    <p className="font-medium mb-1">{fixResult.message}</p>
                    {fixResult.details?.filter(d => d.status === "fixed").map((d, i) => (
                      <p key={i} className="text-slate-300">
                        {d.name}: {d.old_total} → {d.new_total} ({d.diff > 0 ? '+' : ''}{d.diff})
                      </p>
                    ))}
                  </div>
                )}
              </div>
            )}

            {recalcResult && (
              <div className={`mb-3 p-3 rounded-lg text-xs ${recalcResult.error ? 'bg-red-500/10 border border-red-500/30 text-red-400' : 'bg-amber-500/10 border border-amber-500/30 text-amber-300'}`}>
                {recalcResult.error ? (
                  <p>{recalcResult.error}</p>
                ) : (
                  <div>
                    <p className="font-medium mb-2">{recalcResult.message} ({recalcResult.snapshots_corrected} corrected)</p>
                    <div className="overflow-x-auto max-h-48 overflow-y-auto">
                      <table className="w-full text-[10px]">
                        <thead><tr className="border-b border-amber-500/30 text-amber-400">
                          <th className="py-1 px-1 text-left">User</th>
                          <th className="py-1 px-1 text-right">Week</th>
                          <th className="py-1 px-1 text-right">Team Val</th>
                          <th className="py-1 px-1 text-right">Bank</th>
                          <th className="py-1 px-1 text-right">Budget</th>
                        </tr></thead>
                        <tbody>
                          {recalcResult.details?.map((d, i) => (
                            <tr key={i} className="border-b border-slate-700/30">
                              <td className="py-1 px-1 text-white">{d.user}</td>
                              <td className="py-1 px-1 text-right text-slate-400">{d.week}</td>
                              <td className="py-1 px-1 text-right text-slate-300 font-mono">{d.team_value}</td>
                              <td className="py-1 px-1 text-right text-slate-300 font-mono">{d.bank_balance}</td>
                              <td className="py-1 px-1 text-right text-amber-400 font-mono font-bold">{d.budget}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}

            {loadingCaptainLogs ? (
              <div className="flex justify-center py-4">
                <Loader2 className="animate-spin text-emerald-500" size={24} />
              </div>
            ) : captainChangeLogs.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">No captain/vice captain changes recorded yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="captain-change-log-table">
                  <thead>
                    <tr className="border-b border-slate-700 text-slate-400">
                      <th className="py-2 px-2 text-left">Time</th>
                      <th className="py-2 px-2 text-left">User</th>
                      <th className="py-2 px-2 text-center">Impacted week</th>
                      <th className="py-2 px-2 text-left">Type</th>
                      <th className="py-2 px-2 text-left">Old</th>
                      <th className="py-2 px-2 text-left">New</th>
                      <th className="py-2 px-2 text-right">Before</th>
                      <th className="py-2 px-2 text-right">After</th>
                      <th className="py-2 px-2 text-right">Diff</th>
                    </tr>
                  </thead>
                  <tbody>
                    {captainChangeLogs.map((log, i) => (
                      <tr key={log.id || i} className="border-b border-slate-800 hover:bg-slate-800/50">
                        <td className="py-2 px-2 text-slate-400 whitespace-nowrap">
                          {log.timestamp ? new Date(log.timestamp).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'}
                        </td>
                        <td className="py-2 px-2 text-white">{log.user_name || log.user_id?.slice(0, 8)}</td>
                        <td className="py-2 px-2 text-center">
                          {editingWeekLogId === (log.id || i) ? (
                            <input
                              type="number"
                              min="1"
                              value={editingWeekValue}
                              onChange={e => setEditingWeekValue(e.target.value)}
                              onBlur={() => handleSaveWeek(log.id || i)}
                              onKeyDown={e => { if (e.key === 'Enter') handleSaveWeek(log.id || i); if (e.key === 'Escape') setEditingWeekLogId(null); }}
                              autoFocus
                              className="w-10 bg-slate-700 text-white text-center text-[10px] font-mono rounded px-1 py-0.5 border border-emerald-500 outline-none"
                              data-testid={`week-edit-input-${i}`}
                            />
                          ) : (
                            <button
                              onClick={() => { setEditingWeekLogId(log.id || i); setEditingWeekValue(log.week || ""); }}
                              className="px-1.5 py-0.5 rounded bg-slate-700 text-slate-300 text-[10px] font-mono hover:bg-emerald-500/30 hover:text-emerald-300 cursor-pointer border border-transparent hover:border-emerald-500/50 transition-colors"
                              title="Click to edit week"
                              data-testid={`week-edit-btn-${i}`}
                            >
                              W{log.week || '?'}
                            </button>
                          )}
                        </td>
                        <td className="py-2 px-2">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${log.change_type === 'captain' ? 'bg-amber-500/20 text-amber-400' : 'bg-cyan-500/20 text-cyan-400'}`}>
                            {log.change_type === 'captain' ? 'C' : 'VC'}
                          </span>
                        </td>
                        <td className="py-2 px-2 text-slate-400">{log.old_player_name || '-'}</td>
                        <td className="py-2 px-2 text-white">{log.new_player_name || '-'}</td>
                        <td className="py-2 px-2 text-right text-slate-400 font-mono">{log.points_before ?? '-'}</td>
                        <td className="py-2 px-2 text-right text-white font-mono">{log.points_after ?? '-'}</td>
                        <td className={`py-2 px-2 text-right font-mono font-bold ${(log.points_diff || 0) > 0 ? 'text-emerald-400' : (log.points_diff || 0) < 0 ? 'text-red-400' : 'text-slate-500'}`}>
                          {(log.points_diff || 0) > 0 ? '+' : ''}{log.points_diff ?? 0}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="text-xs text-slate-500 mt-2">{captainChangeLogs.length} changes recorded</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, color }) {
  const colors = {
    emerald: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
    cyan: "bg-cyan-500/20 text-cyan-400 border-cyan-500/30",
    amber: "bg-amber-500/20 text-amber-400 border-amber-500/30",
    orange: "bg-orange-500/20 text-orange-400 border-orange-500/30",
    purple: "bg-purple-500/20 text-purple-400 border-purple-500/30",
    blue: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  };
  
  return (
    <div className={`${colors[color]} border rounded-xl p-3 text-center`}>
      <div className="flex justify-center mb-1">{icon}</div>
      <p className="text-2xl font-bold">{value}</p>
      <p className="text-xs opacity-80">{label}</p>
    </div>
  );
}
